import { RecordInfo } from "../data/models/RecordInfo";
import { UserModule } from "@/store/modules/UserModule";
import { formatDateShort } from "../utils/DateFormatter";
import { ProjectDetails } from "@/data/models/ProjectDetails";
import Vue from "vue";
import {
  COLOR_STANDARD_WHITE,
  COLOR_PROJECT_TILE_INCOMPLETE,
  COLOR_PROJECT_TILE_COMPLETE,
  COLOR_PROJECT_TILE_LOCKED,
  COLOR_PROJECT_TILE_ON_HOLD,
} from "./colors";

export interface CardState {
  name: string;
  color: string;
  shouldBeDisplayed: boolean;
}

/**
 * Maps a nullable "step complete" flag to a tile colour.
 * - `true`  → green (complete)
 * - `false` → yellow (reached, not complete)
 * - `null`/`undefined` → `nullColor` (no data for this step)
 *
 * `nullColor` defaults to white to preserve historic behaviour; callers that
 * want an unset step to read as "locked" (e.g. the portal-only / flex builders)
 * pass `COLOR_PROJECT_TILE_LOCKED`.
 */
export function getCardColor(
  from?: boolean | null,
  nullColor: string = COLOR_STANDARD_WHITE
): string {
  if (from === null || from === undefined) {
    return nullColor;
  } else if (from) {
    return COLOR_PROJECT_TILE_COMPLETE;
  } else {
    return COLOR_PROJECT_TILE_INCOMPLETE;
  }
}

export function getRecordedByItem(
  vue: Vue,
  recordInfo: RecordInfo | undefined,
  text: string
): string | null {
  if (!recordInfo) {
    return null;
  }

  const dateText = formatDateShort(recordInfo.recordedAt);
  const user = UserModule.userNicknameFromRecordInfo(recordInfo);

  return vue
    .$t("projectOverview.recordedByItem", [dateText, text, user])
    .toString();
}

export function buildIresProjectNumber(projectDetails: ProjectDetails): string {
  const paddedProjectNumber = projectDetails.internalProjectNumber
    .toString()
    .padStart(4, "0");
  return projectDetails.firmShort + paddedProjectNumber;
}

/**
 * Builds card states for GZ (portal-only / Geräte Zuordnung) projects.
 *
 * Tile mapping:
 * - 1: Approval — green if approved, red if pending
 * - 2–4: Always grey (skipped steps for GZ)
 * - 5: Drying setup complete (step 10)
 * - 6: Measurement progress (step 12/13) — grey not started, yellow incomplete, green finished
 * - 7: Device packup complete (step 15)
 * - 8: Project conclusion complete (step 18)
 *
 * Tiles 5, 6, 7, 8 are locked (grey) until tile 1 is approved. An unset step
 * (null/undefined) also reads as locked (grey) rather than white.
 */
export function getPortalOnlyProjectCardStates(
  isApproved: boolean,
  dryingSetupComplete?: boolean,
  dryingProgressComplete?: boolean | null,
  devicePackupComplete?: boolean,
  projectConclusionComplete?: boolean,
): CardState[] {
  const elements: Array<boolean | undefined> = [
    isApproved,
    undefined,
    undefined,
    undefined,
    dryingSetupComplete ?? undefined,
    dryingProgressComplete ?? undefined,
    devicePackupComplete ?? undefined,
    projectConclusionComplete ?? undefined,
  ];

  return elements.map((element, index) => {
    let color: string;

    if (index === 0) {
      color = isApproved ? COLOR_PROJECT_TILE_COMPLETE : COLOR_PROJECT_TILE_ON_HOLD;
    } else if (index < 4) {
      color = COLOR_PROJECT_TILE_LOCKED;
    } else {
      // Unset (null/undefined) drying steps read as locked (grey) rather than
      // white, so the tile row stays consistent for IRES-Work-created projects.
      color = isApproved
        ? getCardColor(element, COLOR_PROJECT_TILE_LOCKED)
        : COLOR_PROJECT_TILE_LOCKED;
    }

    return {
      shouldBeDisplayed: true,
      name: (index + 1).toString(),
      color: color,
    };
  });
}
